const stateKeys = {
  detail: 'appliance-detail'
}

export const actions = {
  clearStorage() {
    for (const prop in stateKeys) {
      this.$storage.removeUniversal(stateKeys[prop])
    }
  },

  getData(
    { dispatch, commit },
    { applianceId, format, interval, startDateTime }
  ) {
    const startTime = this.$toSecondsFromEpoch(startDateTime)
    const timezoneOffset = startDateTime.getTimezoneOffset()

    return this.$apiAaltra.applianceDetails(
      applianceId,
      format._format,
      interval._period,
      startTime,
      timezoneOffset
    )
  },

  async getLiveData({ dispatch }, { applianceId }) {
    const data = await this.$apiAaltra.applianceLiveData(applianceId)
    if (!data) {
      return
    }

    const lastPower = data.current.power
      ? data.current.power[data.current.power.length - 1]
      : 0

    return {
      current: this.$formatEnergy(lastPower),
      lineChartData: {
        labels:
          data.current.timestamps?.map((timestamp) =>
            this.$formatMinutesAgo(timestamp)
          ) || 0,
        datasets: [
          {
            data: data.current.power?.map(
              // (value) => this.$formatEnergy(value, 'en')
              (value) => value || 0
            )
          }
        ]
      }
    }
  },

  async getApplianceTypes(
    { dispatch },
    { selectedApplianceId, forLogs, forAlerts }
  ) {
    const data = await this.$apiAaltra.applianceTypes()

    let appliancesItems = data.applianceTypes.map((a) => {
      return {
        id: a.id,
        name: a.icon && a.icon.length ? a.name : this.app.i18n.t(a.name)
      }
    })

    if (forLogs) {
      appliancesItems = appliancesItems.filter(
        (a) => this.$applianceType(a.id).logs
      )
    }

    if (forAlerts) {
      appliancesItems.push({
        id: 9001,
        name: this.app.i18n.t('generalConsumption')
      })
    }

    function sortCompare(a, b) {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    }

    appliancesItems.sort(sortCompare)

    // Hide Tumble dryer and Toaster
    if (appliancesItems.length) {
      appliancesItems.forEach((item, itemIdx) => {
        if (item.id === 43 || item.id === 28) {
          appliancesItems.splice(itemIdx, 1)
        }
      })
    }

    let selectedApplianceItem = { id: 0, name: this.app.i18n.t('all') }
    appliancesItems.unshift(selectedApplianceItem)

    if (selectedApplianceId) {
      selectedApplianceItem = appliancesItems.find(
        (appliance) => appliance.id === selectedApplianceId
      )
    }

    return { appliancesItems, selectedApplianceItem }
  },

  async setApplianceType(_, data) {
    const axios = await this.$apiAaltra.updateAplianceType(data)
    const axiosErrors = axios?.response?.data?.errors

    let errors = []
    if (axios?.response?.status === 400 && axiosErrors) {
      for (const prop in axiosErrors) {
        if (Object.prototype.hasOwnProperty.call(axiosErrors, prop)) {
          errors = errors.concat(axiosErrors[prop])
        }
      }
    }

    return errors
  },

  getApplianceDetails() {
    return this.$storage.getUniversal(stateKeys.detail)
  },

  setApplianceDetails(_, data) {
    this.$storage.setUniversal(stateKeys.detail, data)
  },

  map({ dispatch }, { data, interval, format, mapForHome }) {
    const applianceType = this.$applianceType(data.id)
    const unit = format.value ? this.$currency.get() : this.app.i18n.t('kwh')
    const textUnit = mapForHome ? this.app.i18n.t('w') : unit
    const iconIsCustom = !!data.icon
    const applianceName = iconIsCustom
      ? data.name
      : this.app.i18n.t(applianceType.name)

    const result = {
      id: data.id,
      name: applianceName,
      text: [
        applianceName,
        this.$formatUnit(this.$formatEnergy(data.value), textUnit),
        this.$formatPercentage(data.percentile)
      ],
      applianceIcon: {
        name: data.icon || applianceType.icon,
        circle: applianceType.color,
        size: 64
      }
    }

    if (interval) {
      result.average = [
        interval.averageLabel,
        this.$formatUnit(
          this.$formatEnergy(parseFloat(data.history.average)),
          unit
        )
      ]

      result.total = {
        text: this.app.i18n.t(
          format.text === 'grid' ? 'totalConsumption' : 'totalCost'
        ),
        value: this.$formatUnit(this.$formatEnergy(data.history.total), unit)
      }
    }

    return result
  },

  mapOneToBarChartData(_, { data, period }) {
    if (!data) {
      return { datasets: [] }
    }
    return {
      labels: this.$labelsByPeriod(data.timestamps, period),
      datasets: [
        {
          data: data.values?.map((x) => this.$formatEnergy(x || 0, 'en')) || []
        }
      ]
    }
  },

  async mapManyToChartData({ dispatch }, { data, format }) {
    if (!data) {
      return { labels: [], datasets: [{ data: [] }] }
    }

    const sorted = await dispatch('sort', data)

    return {
      labels: sorted.map((appliance) => appliance.id),
      datasets: [{ data: sorted.map((appliance) => appliance.value) }],
      total: this.$formatEnergy(sorted.reduce((a, b) => a + b.value, 0)),
      unit: format.value ? this.$currency.get() : this.app.i18n.t('kwh')
    }
  },

  async mapManyToListData({ dispatch }, { data, format, mapForHome }) {
    const applianceListData = []
    const applianceAlwaysOnData = []

    if (data) {
      const sorted = await dispatch('sort', data)

      for (let i = 0; i < sorted.length; i++) {
        const applianceData = sorted[i]
        if (!applianceData.value) {
          continue
        }
        const appliance = await dispatch('map', {
          data: applianceData,
          format,
          mapForHome
        })
        appliance.icon = { ...appliance.applianceIcon, size: 32 }
        appliance.route = {
          name: 'energy-appliance-applianceId',
          params: {
            applianceId: applianceData.id
          }
        }

        if (['others', 'alwaysOn'].includes(applianceData.name)) {
          applianceAlwaysOnData.push(appliance)
        } else {
          applianceListData.push(appliance)
        }
      }
    }

    return {
      regular: applianceListData,
      alwaysOn: applianceAlwaysOnData
    }
  },

  sort(_, data) {
    function compare(a, b) {
      if (b.value > a.value) {
        return 1
      }
      if (a.value > b.value) {
        return -1
      }

      return 0
    }

    const appliances = data.filter(
      (appliance) => !['others', 'alwaysOn'].includes(appliance.name)
    )
    const others = data.filter((appliance) =>
      ['others', 'alwaysOn'].includes(appliance.name)
    )

    appliances.sort(compare)
    others.sort(compare)

    return [...appliances].concat(others)
  }
}
